<template>
  <div class="max-w-xl mx-auto">
    <div
        class="relative overflow-hidden rounded-3xl border border-slate-200/70
             bg-gradient-to-br from-slate-50 via-white to-slate-100
             shadow-xl"
    >
      <div class="pointer-events-none absolute inset-0 overflow-hidden">
        <div class="absolute -left-10 -top-10 w-32 h-32 bg-cyan-300/20 blur-3xl rounded-full"></div>
        <div class="absolute -right-10 bottom-0 w-40 h-40 bg-violet-400/20 blur-3xl rounded-full"></div>
      </div>

      <div
          v-if="loading"
          class="absolute inset-0 bg-slate-900/30 backdrop-blur-sm flex items-center justify-center z-20"
      >
        <div
            class="rounded-2xl bg-white shadow-2xl px-4 py-3 flex items-center gap-3 border border-slate-200 max-w-xs"
        >
          <svg
              class="w-5 h-5 animate-spin text-sky-500"
              fill="none"
              viewBox="0 0 24 24"
          >
            <circle
                class="opacity-25"
                cx="12" cy="12" r="10"
                stroke="currentColor" stroke-width="4"
            ></circle>
            <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 00-8 8h3z"
            ></path>
          </svg>
          <div class="flex flex-col">
            <span class="text-xs font-semibold text-slate-800">
              در حال ساخت گزارش...
            </span>
            <span class="text-[10px] text-slate-500">
              یک تب جدید برای دانلود گزارش باز می‌شود.
            </span>
          </div>
        </div>
      </div>

      <div class="relative p-5">
        <div class="mb-4 flex items-center justify-between gap-3">
          <div class="flex-1">
            <h2 class="text-sm font-semibold text-slate-900 tracking-wide">
              گزارش موجودی محصولات بر اساس برند
            </h2>
            <p class="text-[11px] text-slate-500 mt-1 leading-relaxed">
              یک برند را انتخاب کن و خروجی دلخواهت را به صورت
              <span class="font-semibold text-sky-600">چاپ</span>،
              <span class="font-semibold text-rose-600">PDF</span>
              یا
              <span class="font-semibold text-emerald-600">اکسل</span>
              بگیر.
            </p>
          </div>
        </div>

        <div class="space-y-4">
          <div>
            <label
                for="brand"
                class="block text-xs font-medium text-slate-700 mb-1"
            >
              انتخاب برند
            </label>
            <div class="relative">
              <brand-select
                  scope="local"
                  id="brand"
                  v-model="form.brand"
                  class="w-full px-2 py-1 text-slate-800"
              ></brand-select>
            </div>
            <p class="mt-1 text-[10px] text-slate-500">
              فقط محصولات موجود در گزارش هستند
            </p>
          </div>

          <div class="grid grid-cols-1 sm:grid-cols-[auto,120px] gap-2 items-end">
            <div>
              <label
                  for="percent"
                  class="block text-xs font-medium text-slate-700 mb-1"
              >
                درصد سود روی قیمت پایه
              </label>
            </div>

            <div>
              <label
                  for="percent"
                  class="block text-[10px] font-medium text-slate-500 mb-1 text-right"
              >
                درصد (٪)
              </label>
              <input
                  id="percent"
                  type="number"
                  min="1"
                  max="100"
                  v-model.number="form.percent"
                  class="block w-full rounded-md text-center border border-slate-200 bg-slate-200 px-2 py-1.5
                         text-xs text-slate-900 focus:outline-none focus:ring-1
                         focus:ring-sky-500 focus:border-sky-500"
                  placeholder="مثلاً 5"
              >
              <p class="mt-0.5 text-[10px] text-slate-400 text-right">
                مقدار فعلی: <span class="font-semibold">{{ percentValue }}٪</span>
              </p>
            </div>
          </div>

          <!-- Export Buttons -->
          <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-1">
            <div class="flex flex-col gap-1">
              <span class="text-[11px] text-slate-500">
                انتخاب فرمت خروجی:
              </span>
              <div class="flex flex-wrap items-center gap-1.5 text-[10px] text-slate-600">
                <span class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 border border-slate-200">
                  <span class="w-1.5 h-1.5 rounded-full bg-sky-500"></span> چاپ
                </span>
                <span class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 border border-slate-200">
                  <span class="w-1.5 h-1.5 rounded-full bg-rose-500"></span> PDF
                </span>
                <span class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 border border-slate-200">
                  <span class="w-1.5 h-1.5 rounded-full bg-emerald-500"></span> اکسل
                </span>
              </div>
            </div>

            <div class="flex flex-wrap items-center gap-2 justify-end">
              <!-- HTML -->
              <button
                  type="button"
                  @click="download('html')"
                  :disabled="!hasBrand || loading"
                  class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl
                       border text-[11px] font-medium
                       border-sky-200 bg-sky-50 text-sky-700
                       hover:bg-sky-100 hover:border-sky-300
                       disabled:opacity-40 disabled:cursor-not-allowed
                       transition-colors"
                  title="خروجی  چاپ"
              >
                <svg
                    class="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                  <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M7 17v-6a2 2 0 012-2h6a2 2 0 012 2v6M9 13h6M9 17h6M9 7h.01M15 7h.01"
                  />
                </svg>
                <span>چاپ </span>
              </button>

              <button
                  type="button"
                  @click="download('pdf')"
                  :disabled="!hasBrand || loading"
                  class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl
                       border text-[11px] font-medium
                       border-rose-200 bg-rose-50 text-rose-700
                       hover:bg-rose-100 hover:border-rose-300
                       disabled:opacity-40 disabled:cursor-not-allowed
                       transition-colors"
                  title="خروجی PDF"
              >
                <svg
                    class="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                  <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                  />
                  <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M9 13h4M9 17h2"
                  />
                </svg>
                <span>PDF</span>
              </button>

              <!-- XLSX -->
              <button
                  type="button"
                  @click="download('xlsx')"
                  :disabled="!hasBrand || loading"
                  class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl
                       border text-[11px] font-medium
                       border-emerald-200 bg-emerald-50 text-emerald-700
                       hover:bg-emerald-100 hover:border-emerald-300
                       disabled:opacity-40 disabled:cursor-not-allowed
                       transition-colors"
                  title="خروجی اکسل"
              >
                <svg
                    v-if="!loading"
                    class="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                  <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M9 17l-3-3m0 0l3-3m-3 3h8m4-5h-6m6 10h-6M9 7V5a2 2 0 012-2h6a2 2 0 012 2v14a2 2 0 01-2 2h-6a2 2 0 01-2-2v-2"
                  />
                </svg>

                <svg
                    v-else
                    class="w-4 h-4 animate-spin"
                    fill="none"
                    viewBox="0 0 24 24"
                >
                  <circle
                      class="opacity-25"
                      cx="12" cy="12" r="10"
                      stroke="currentColor" stroke-width="4"
                  ></circle>
                  <path
                      class="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 00-8 8h3z"
                  ></path>
                </svg>

                <span>اکسل</span>
              </button>
            </div>
          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import BrandSelect from "@/components/selects/BrandSelect";

export default {
  name: 'ProductInventoryByBrandReport',
  components: { BrandSelect },
  data () {
    return {
      form: {
        brand: null,
        percent: 5
      },
      loading: false
    }
  },
  computed: {
    hasBrand () {
      return !!this.getBrandId()
    },
    percentValue () {
      let v = parseInt(this.form.percent, 10)

      if (isNaN(v)) v = 5
      if (v < 1) v = 1
      if (v > 100) v = 100

      return v
    }
  },
  methods: {
    getBrandId () {
      if (this.form.brand && typeof this.form.brand === 'object') {
        return this.form.brand.id || this.form.brand.pk || null
      }
      return this.form.brand
    },
    download (format) {
      const brandId = this.getBrandId()
      if (!brandId || this.loading) return

      this.loading = true

      const base = this.endpoint(`product/export/brand-products/${brandId}/${format}`)
      const url = `${base}?token=${this.token}&percent=${this.percentValue}`

      const newWindow = window.open(url, '_blank')

      if (!newWindow) {
        window.location.href = url
      }

      setTimeout(() => {
        this.loading = false
      }, 2000)
    }
  }
}
</script>
